import React from 'react';
import { ResponsiveStream } from '@nivo/stream';

const AllianceVsRivalAnalysis = ({ geopoliticalData, selectedMineral = 'Lithium' }) => {
  
  // Transform data for Nivo Stream chart
  const transformDataForStream = () => {
    if (!geopoliticalData?.groups) return [];
    
    // Create time series data (mock for now - can be enhanced with real historical data)
    const years = ['2020', '2021', '2022', '2023', '2024'];
    
    const data = years.map((year, index) => {
      const yearData = { 
        year: year,
        index: index // Add index for Nivo
      };
      
      geopoliticalData.groups.forEach(group => {
        // Add some variation over time for demonstration
        const baseValue = group.importDependencyPercentage || 0; // Keep as percentage (0-100)
        const variation = (Math.random() - 0.5) * 5; // ±2.5% variation
        yearData[group.label] = Math.max(0, Math.min(100, baseValue + variation));
      });
      
      return yearData;
    });
    
    return data;
  };

  // Get keys (group labels) for the stream chart
  const getStreamKeys = () => {
    if (!geopoliticalData?.groups) return [];
    return geopoliticalData.groups.map(group => group.label);
  };

  // Get colors for each group
  const getStreamColors = () => {
    if (!geopoliticalData?.groups) return [];
    return geopoliticalData.groups.map(group => group.color);
  };

  // Calculate current distribution percentages
  const getCurrentDistribution = () => {
    if (!geopoliticalData?.groups) return [];
    
    const total = geopoliticalData.groups.reduce((sum, group) => 
      sum + (group.importDependencyPercentage || 0), 0);
    
    return geopoliticalData.groups
      .filter(group => group.importDependencyPercentage > 0)
      .map(group => ({
        ...group,
        percentage: total > 0 ? (group.importDependencyPercentage / total) * 100 : 0
      }))
      .sort((a, b) => b.percentage - a.percentage);
  };

  const streamData = transformDataForStream();
  const streamKeys = getStreamKeys();
  const streamColors = getStreamColors();
  const currentDistribution = getCurrentDistribution();

  // No data state
  if (!geopoliticalData?.groups || geopoliticalData.groups.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center py-12">
          <div className="text-yellow-500 text-lg mb-2">⚠️ No Data Available</div>
          <p className="text-gray-600">No geopolitical analysis data available for {selectedMineral}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-600 p-6">
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <div className="w-4 h-4 bg-purple-500 rounded mr-2"></div>
          <h3 className="text-xl font-bold text-white">
            Alliance vs Rival Analysis
          </h3>
        </div>
        <p className="text-sm text-gray-300">
          Import dependency distribution across geopolitical groups over time
        </p>
      </div>

      {/* Stream Chart */}
      <div style={{ height: '300px' }} className="mb-6">
        <ResponsiveStream
          data={streamData}
          keys={streamKeys}
          margin={{ top: 20, right: 110, bottom: 50, left: 60 }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            orient: 'bottom',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Year',
            legendOffset: 36,
            legendPosition: 'middle',
            format: (value, index) => {
              const years = ['2020', '2021', '2022', '2023', '2024'];
              return years[value] || years[index] || value;
            }
          }}
          axisLeft={{
            orient: 'left',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Import Dependency (%)',
            legendOffset: -40,
            legendPosition: 'middle',
            format: (value) => `${value.toFixed(0)}%`
          }}
          yScale={{
            type: 'linear',
            min: 0,
            max: 100
          }}
          offsetType="none"
          colors={streamColors}
          fillOpacity={0.85}
          borderColor={{ theme: 'background' }}
          borderWidth={2}
          legends={[
            {
              anchor: 'bottom-right',
              direction: 'column',
              translateX: 100,
              itemWidth: 80,
              itemHeight: 20,
              itemTextColor: '#374151',
              symbolSize: 12,
              symbolShape: 'circle',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemTextColor: '#000'
                  }
                }
              ]
            }
          ]}
          tooltip={(tooltipData) => {
            console.log('Tooltip data:', tooltipData);
            
            // Handle different tooltip data structures
            const layer = tooltipData.layer || tooltipData.slice?.points?.[0] || tooltipData;
            
            if (!layer) return null;
            
            return (
              <div className="bg-gray-800 text-white p-3 rounded-lg shadow-lg border border-gray-600">
                <div className="font-semibold text-white">{layer.id || 'Unknown'}</div>
                <div className="text-sm text-gray-300 mt-1">
                  Dependency: {layer.value ? layer.value.toFixed(1) : '0.0'}%
                </div>
              </div>
            );
          }}
          theme={{
            background: 'transparent',
            text: {
              fontSize: 11,
              fill: '#d1d5db'
            },
            tooltip: {
              container: {
                background: '#1f2937',
                color: '#ffffff',
                fontSize: '12px',
                borderRadius: '8px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
                border: '1px solid #4b5563'
              }
            },
            axis: {
              domain: {
                line: {
                  stroke: '#6b7280',
                  strokeWidth: 1
                }
              },
              legend: {
                text: {
                  fontSize: 12,
                  fill: '#f3f4f6',
                  fontWeight: 500
                }
              },
              ticks: {
                line: {
                  stroke: '#6b7280',
                  strokeWidth: 1
                },
                text: {
                  fontSize: 11,
                  fill: '#d1d5db'
                }
              }
            },
            legends: {
              text: {
                fontSize: 11,
                fill: '#d1d5db'
              }
            }
          }}
        />
      </div>

      {/* Legend Below Chart */}
      <div className="mt-4 pt-3">
        <div className="flex flex-wrap justify-center gap-6">
          {currentDistribution.map((group, index) => (
            <div key={group.id} className="flex items-center">
              <div 
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: group.color }}
              ></div>
              <span className="text-sm text-gray-300">
                {group.label} ({group.percentage.toFixed(0)}%)
              </span>
            </div>
          ))}
        </div>
      </div>


    </div>
  );
};

export default AllianceVsRivalAnalysis;
